import { LOCAL_STORAGE_KEYS } from './local-storage-keys'
import { SESSION_STORAGE_KEYS } from './session-storage-keys'
import { useLogger } from '@booksy-com/vue-tools'

const logger = useLogger('storage')

const STORAGE = {
  SESSION: 'session',
  LOCAL: 'local',
} as const

type StorageType = (typeof STORAGE)[keyof typeof STORAGE]

const storageTypes: Record<StorageType, Storage> = {
  [STORAGE.SESSION]: sessionStorage,
  [STORAGE.LOCAL]: localStorage,
}

const storageKeys: Record<StorageType, typeof LOCAL_STORAGE_KEYS | typeof SESSION_STORAGE_KEYS> = {
  [STORAGE.SESSION]: SESSION_STORAGE_KEYS,
  [STORAGE.LOCAL]: LOCAL_STORAGE_KEYS,
}

export type UseStorageReturn = {
  setItem: (key: string, value: unknown) => void
  getItem: (key: string) => unknown
  removeItem: (key: string) => void
  clear: () => void
  KEYS: typeof LOCAL_STORAGE_KEYS | typeof SESSION_STORAGE_KEYS
}

export function useStorage (storageType: StorageType = STORAGE.LOCAL): UseStorageReturn | undefined {
  if (!Object.values(STORAGE).includes(storageType)) {
    logger.error('Invalid storageType')
    return undefined
  }

  const storage = storageTypes[storageType]
  const KEYS = storageKeys[storageType]

  const keyExist = (key: string): boolean => {
    return Object.values(KEYS).includes(key)
      ? true
      : (logger.warn('useStorage: expected key does not exist'), false)
  }

  const setItem = (key: string, value: unknown): void => {
    if (keyExist(key)) {
      storage.setItem(key, JSON.stringify(value))
    }
  }

  const getItem = (key: string): unknown => {
    if (!keyExist(key)) return null

    try {
      return JSON.parse(storage.getItem(key) as string)
    } catch (error) {
      logger.error(`Error parsing storage key "${key}":`, error)
      return null
    }
  }

  const removeItem = (key: string): void => {
    if (keyExist(key)) {
      storage.removeItem(key)
    }
  }

  const clear = (): void => {
    storage.clear()
  }

  return { setItem, getItem, removeItem, clear, KEYS }
}
